/**
 * discountVoidDataSource.ts — WatermelonDB → DiscountVoidInput bridge.
 *
 * Unlike the other reports, this one *keeps* voided invoices — auditing them is
 * the point. It loads SALE invoices raised in the range (any status), marks
 * which are voided, parses the void reason from the note, and resolves the
 * biller's name.
 */

import { Q } from '@nozbe/watermelondb';
import { database } from '@/db';
import { fetchInChunks } from '@/db/fetchInChunks';
import { Invoice, VOIDED_INVOICE_STATUSES } from '@/db/models/Billing';
import User from '@/db/models/User';
import { parseVoidReason, type DiscountVoidInput } from './discountVoid';

/** Load a store's discount + void audit inputs for the range `[from, to)`. */
export async function loadDiscountVoid(
  storeId: string,
  from: Date,
  to: Date,
): Promise<DiscountVoidInput> {
  const invoices = await database.get<Invoice>('invoices').query(
    Q.where('store_id', storeId),
    Q.where('is_deleted', false),
    Q.where('type', 'SALE'),
    Q.where('created_at', Q.gte(from.getTime())),
    Q.where('created_at', Q.lt(to.getTime())),
  ).fetch();

  // Resolve biller names.
  const staffIds = [...new Set(invoices.map((i) => i.createdById).filter(Boolean))];
  const users = await fetchInChunks(staffIds, (batch) =>
    database.get<User>('users').query(Q.where('id', Q.oneOf(batch))).fetch(),
  );
  const staffNames = new Map(users.map((u) => [u.id, u.name ?? u.phone ?? 'Unknown']));

  return {
    invoices: invoices.map((i) => ({
      invoiceNumber: i.invoiceNumber,
      createdAt: i.createdAt,
      createdById: i.createdById,
      isVoided: VOIDED_INVOICE_STATUSES.includes(i.status),
      discountAmount: i.discountAmount,
      taxableAmount: i.taxableAmount,
      totalAmount: i.totalAmount,
      voidReason: parseVoidReason(i.notes),
    })),
    staffNames,
  };
}
